/**
 * PaginationFaultTracker - Utility for tracking pagination faults
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for tracking when pagination faults occur,
 * enabling time-based backoff and fault recovery strategies.
 */

/**
 * Track a pagination fault for a specific document
 * 
 * Records the timestamp when a pagination fault occurred for a document,
 * enabling time-based backoff strategies.
 * 
 * @param faultMap - Map of document hash to fault timestamp
 * @param docHash - Document hash identifier
 * @param globalFaultTime - Reference to global fault timestamp (updated by this function)
 */
export function trackPaginationFault(
  faultMap: Map<string, number>,
  docHash: string,
  globalFaultTime: { value: number }
): void {
  const now = Date.now();
  faultMap.set(docHash, now);
  globalFaultTime.value = now;
  
  console.log(`📊 [PaginationFaultTracker] Tracked fault for doc ${docHash.substring(0, 8)}... at ${now}`);
}

/**
 * Get the last fault timestamp for a document
 * 
 * @param faultMap - Map of document hash to fault timestamp
 * @param docHash - Document hash identifier
 * @returns Timestamp of last fault, or undefined if no fault recorded
 */
export function getLastFaultTime(
  faultMap: Map<string, number>,
  docHash: string
): number | undefined {
  return faultMap.get(docHash);
}

/**
 * Check if enough time has passed since last fault (for backoff)
 * 
 * @param faultMap - Map of document hash to fault timestamp
 * @param docHash - Document hash identifier
 * @param backoffMs - Minimum milliseconds to wait before retry
 * @returns True if enough time has passed, false otherwise
 */
export function canRetryAfterFault(
  faultMap: Map<string, number>,
  docHash: string,
  backoffMs: number
): boolean {
  const lastFault = getLastFaultTime(faultMap, docHash);
  if (!lastFault) {
    return true; // No fault recorded, can retry
  }
  
  const timeSinceFault = Date.now() - lastFault;
  const canRetry = timeSinceFault >= backoffMs;
  
  if (!canRetry) {
    console.log(`⏳ [PaginationFaultTracker] Backoff active: ${timeSinceFault}ms < ${backoffMs}ms`);
  }
  
  return canRetry;
}

